import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Truck, Plus, Trash2, CheckCircle, Clock, User, Phone, AlertTriangle, X, CheckCircle2 } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { supabase } from '../../lib/supabase';

export default function BarrelDepositPlanningSystem() {
  const navigate = useNavigate();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewDepositModal, setShowNewDepositModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, clientName, saleId }
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }
  const [newDeposit, setNewDeposit] = useState({
    clientName: '',
    clientPhone: '',
    clientAddress: '',
    clientCity: '',
    barrelType: 'noir_270l',
    quantity: 1,
    scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
    deliveryNotes: ''
  });

  useEffect(() => {
    loadDeposits();
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const loadDeposits = async () => {
    try {
      setLoading(true);
      const data = await barrelService?.getDeposits();
      setDeposits(data);
    } catch (error) {
      console.error('Error loading deposits:', error);
      showToast('error', 'Erreur lors du chargement des dépôts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeposit = async () => {
    try {
      const inventory = await barrelService?.getInventory();
      const barrelPrice = inventory?.find(i => i?.barrelType === newDeposit?.barrelType)?.priceEuros || 35;
      const totalPrice = newDeposit?.quantity * barrelPrice;

      await barrelService?.createDeposit({
        ...newDeposit,
        totalPrice,
        depositStatus: 'scheduled',
        paymentStatus: 'pending'
      });
      
      await loadDeposits();
      setShowNewDepositModal(false);
      setNewDeposit({
        clientName: '',
        clientPhone: '',
        clientAddress: '',
        clientCity: '',
        barrelType: 'noir_270l',
        quantity: 1,
        scheduledDate: new Date()?.toISOString()?.split('T')?.[0],
        deliveryNotes: ''
      });
      showToast('success', 'Dépôt planifié avec succès');
    } catch (error) {
      console.error('Error creating deposit:', error);
      showToast('error', 'Erreur lors de la création du dépôt');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const updates = { depositStatus: status };
      if (status === 'delivered') {
        updates.deliveryDate = new Date()?.toISOString()?.split('T')?.[0];
      }
      await barrelService?.updateDeposit(id, updates);
      await loadDeposits();
    } catch (error) {
      console.error('Error updating deposit status:', error);
      showToast('error', 'Erreur lors de la mise à jour du statut');
    }
  };

  const openDeleteConfirm = (deposit) => {
    setDeleteConfirm({
      id: deposit?.id,
      clientName: deposit?.clientName,
      saleId: deposit?.saleId || deposit?.sale_id || null
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      // 1. Delete the barrel deposit
      const { error: depositError } = await supabase
        ?.from('barrel_deposits')
        ?.delete()
        ?.eq('id', deleteConfirm?.id);

      if (depositError) throw depositError;

      // 2. If linked to a barrel_sale, delete it too
      if (deleteConfirm?.saleId) {
        const { error: saleError } = await supabase
          ?.from('barrel_sales')
          ?.delete()
          ?.eq('id', deleteConfirm?.saleId);

        if (saleError) {
          console.warn('Could not delete linked barrel sale:', saleError?.message);
          // Non-blocking: deposit is already deleted
        }
      }

      // 3. Update UI immediately without reload
      setDeposits(prev => prev?.filter(d => d?.id !== deleteConfirm?.id));
      setDeleteConfirm(null);
      showToast('success', `La commande de ${deleteConfirm?.clientName} a été supprimée avec succès.`);
    } catch (error) {
      console.error('Error deleting deposit:', error);
      showToast('error', `Erreur lors de la suppression : ${error?.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const filteredDeposits = deposits?.filter(deposit => {
    let periodMatch = true;
    if (filterPeriod === 'today') {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      periodMatch = deposit?.scheduledDate === today;
    } else if (filterPeriod === 'week') {
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo?.setDate(today?.getDate() - 7);
      const depositDate = new Date(deposit?.scheduledDate);
      periodMatch = depositDate >= weekAgo && depositDate <= today;
    } else if (filterPeriod === 'custom') {
      periodMatch = deposit?.scheduledDate === selectedDate;
    }

    let searchMatch = true;
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      searchMatch = 
        deposit?.clientName?.toLowerCase()?.includes(search) ||
        deposit?.clientPhone?.toLowerCase()?.includes(search) ||
        deposit?.clientAddress?.toLowerCase()?.includes(search) ||
        deposit?.clientCity?.toLowerCase()?.includes(search);
    }

    return periodMatch && searchMatch;
  });

  const groupedByDate = filteredDeposits?.reduce((groups, deposit) => {
    const date = deposit?.scheduledDate;
    if (!groups?.[date]) {
      groups[date] = [];
    }
    groups?.[date]?.push(deposit);
    return groups;
  }, {});

  const getBarrelLabel = (type) => {
    return type === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L';
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: 'bg-blue-100 text-blue-800',
      en_route: 'bg-yellow-100 text-yellow-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      scheduled: 'Planifié',
      en_route: 'En route',
      delivered: 'Livré',
      cancelled: 'Annulé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges?.[status] || badges?.scheduled}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const scheduledCount = deposits?.filter(d => d?.depositStatus === 'scheduled')?.length || 0;
  const enRouteCount = deposits?.filter(d => d?.depositStatus === 'en_route')?.length || 0;
  const deliveredCount = deposits?.filter(d => d?.depositStatus === 'delivered')?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white transition-all ${toast?.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast?.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 hover:opacity-75">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-green-600" />
            Planning Dépôt Barriques
          </h1>
          <p className="mt-2 text-gray-600">
            Planification et suivi des livraisons de barriques vides aux clients
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Planifiés</p>
                <p className="text-2xl font-bold text-blue-600">{scheduledCount}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En route</p>
                <p className="text-2xl font-bold text-yellow-600">{enRouteCount}</p>
              </div>
              <Truck className="w-10 h-10 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Livrés</p>
                <p className="text-2xl font-bold text-green-600">{deliveredCount}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Période
              </label>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Toutes les dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="custom">Date personnalisée</option>
              </select>
            </div>

            {filterPeriod === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date spécifique</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Rechercher
              </label>
              <input
                type="text"
                placeholder="Nom, téléphone, adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <strong className="text-gray-900">{filteredDeposits?.length}</strong> dépôt(s) trouvé(s)
            </div>
            <button
              onClick={() => setShowNewDepositModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Plus className="w-5 h-5" />
              Nouveau Dépôt
            </button>
          </div>
        </div>

        {/* Deposits Table */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-6 h-6 text-green-600" />
              Dépôts de Barriques Planifiés
            </h2>
          </div>

          {filteredDeposits?.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aucun dépôt planifié pour cette période</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {Object.entries(groupedByDate)
                ?.sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                ?.map(([date, depositsForDate]) => (
                  <div key={date}>
                    {/* Date Header */}
                    <div className="px-6 py-3 bg-gray-100">
                      <h3 className="text-sm font-semibold text-gray-700">
                        {formatDate(date)} ({depositsForDate?.length} dépôt{depositsForDate?.length > 1 ? 's' : ''})
                      </h3>
                    </div>

                    {/* Deposits for this date */}
                    <div className="divide-y divide-gray-100">
                      {depositsForDate?.map((deposit) => (
                        <div key={deposit?.id} className="px-6 py-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                              {/* Client Info */}
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <User className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm font-medium text-gray-900">{deposit?.clientName}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Phone className="w-4 h-4" />
                                  {deposit?.clientPhone}
                                </div>
                              </div>

                              {/* Address */}
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-900">{deposit?.clientAddress}</span>
                                </div>
                                {deposit?.clientCity && (
                                  <div className="text-sm text-gray-500 ml-6">{deposit?.clientCity}</div>
                                )}
                              </div>

                              {/* Barrel Info */}
                              <div>
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                  {getBarrelLabel(deposit?.barrelType)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Quantité: {deposit?.quantity} × {(deposit?.totalPrice / deposit?.quantity)?.toFixed(2)}€
                                </div>
                                <div className="text-sm font-semibold text-green-600">
                                  Total: {deposit?.totalPrice?.toFixed(2)}€
                                </div>
                              </div>

                              {/* Status */}
                              <div>
                                <div className="mb-2">{getStatusBadge(deposit?.depositStatus)}</div>
                                {deposit?.deliveryNotes && (
                                  <div className="text-xs text-gray-500 italic">
                                    Note: {deposit?.deliveryNotes}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 ml-4">
                              {deposit?.depositStatus === 'scheduled' && (
                                <button
                                  onClick={() => handleUpdateStatus(deposit?.id, 'en_route')}
                                  className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition"
                                  title="Marquer en route"
                                >
                                  <Truck className="w-5 h-5" />
                                </button>
                              )}
                              {deposit?.depositStatus === 'en_route' && (
                                <button
                                  onClick={() => handleUpdateStatus(deposit?.id, 'delivered')}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded transition"
                                  title="Marquer livré"
                                >
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                              )}
                              <button
                                onClick={() => openDeleteConfirm(deposit)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                                title="Supprimer cette commande"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Supprimer la commande</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                Voulez-vous vraiment supprimer la commande de{' '}
                <strong className="text-gray-900">{deleteConfirm?.clientName}</strong> ?
              </p>
              <p className="text-sm text-red-600 mt-2 font-medium">
                ⚠️ Cette action supprimera définitivement le dépôt et les données liées dans Supabase.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-60 disabled:cursor-not-allowed font-medium"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Oui, supprimer
                  </>
                )}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-60"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Deposit Modal */}
      {showNewDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Planifier un Dépôt de Barrique</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du client *</label>
                <input
                  type="text"
                  value={newDeposit?.clientName}
                  onChange={(e) => setNewDeposit({ ...newDeposit, clientName: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Nom complet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                <input
                  type="tel"
                  value={newDeposit?.clientPhone}
                  onChange={(e) => setNewDeposit({ ...newDeposit, clientPhone: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="+33..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse de livraison *</label>
                <input
                  type="text"
                  value={newDeposit?.clientAddress}
                  onChange={(e) => setNewDeposit({ ...newDeposit, clientAddress: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Adresse complète"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input
                  type="text"
                  value={newDeposit?.clientCity}
                  onChange={(e) => setNewDeposit({ ...newDeposit, clientCity: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Ville"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de livraison *</label>
                <input
                  type="date"
                  value={newDeposit?.scheduledDate}
                  onChange={(e) => setNewDeposit({ ...newDeposit, scheduledDate: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de barrique *</label>
                <select
                  value={newDeposit?.barrelType}
                  onChange={(e) => setNewDeposit({ ...newDeposit, barrelType: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="noir_270l">Barrique Noire 270L (35€)</option>
                  <option value="bleu_220l">Barrique Bleue 220L (40€)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                <input
                  type="number"
                  min="1"
                  value={newDeposit?.quantity}
                  onChange={(e) => setNewDeposit({ ...newDeposit, quantity: parseInt(e?.target?.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes de livraison</label>
                <textarea
                  value={newDeposit?.deliveryNotes}
                  onChange={(e) => setNewDeposit({ ...newDeposit, deliveryNotes: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="3"
                  placeholder="Instructions spéciales, accès, etc."
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleCreateDeposit}
                disabled={!newDeposit?.clientName || !newDeposit?.clientPhone || !newDeposit?.clientAddress}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Planifier le dépôt
              </button>
              <button
                onClick={() => setShowNewDepositModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}